/**
 * Radio Block Renderer
 * Renders single choice selections using RadioGroup
 */

import React from "react";
import { RadioGroup } from "@strivelabs/ui-blocks";
import type { UIBlock, UIActionEvent } from "@/types/ui-blocks";

export interface RadioBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** When true, the component is disabled */
  readOnly?: boolean;
}

export function RadioBlockRenderer({
  block,
  onUIAction,
  readOnly = false
}: RadioBlockRendererProps) {
  const [disabled, setDisabled] = React.useState(readOnly);
  const [selectedValue, setSelectedValue] = React.useState<string | null>(
    block.type === 'radio' ? block.content.selectedValue || null : null
  );

  // Reset local state when the incoming radio block changes
  const blockContent = block.type === 'radio' ? block.content : null;
  React.useEffect(() => {
    setSelectedValue(blockContent?.selectedValue || null);
    // Don't reset disabled if readOnly is true
    if (!readOnly) {
      setDisabled(false);
    }
  }, [blockContent?.label, blockContent?.selectedValue, readOnly]);

  if (block.type !== 'radio') return null;

  const { content } = block;

  const handleApply = (value: string | null) => {
    if (disabled || value === null) return;

    // Find the corresponding label for display
    const option = content.options.find(opt => opt.value === value);
    const label = option?.label || value;

    onUIAction({
      type: "ui_action",
      component: "radio",
      label: content.label,
      state: { value, label }
    });

    setDisabled(true);
  };

  const handleCancel = () => {
    if (disabled) return;
    // Reset to initial value
    setSelectedValue(content.selectedValue || null);
    setDisabled(true);
  };

  // Convert RadioOption[] format
  const radioOptions = content.options.map(opt => ({
    value: opt.value,
    label: opt.label,
    description: opt.description,
    disabled: opt.disabled
  }));

  return (
    <RadioGroup
      options={radioOptions}
      selectedValue={selectedValue}
      onApply={handleApply}
      onCancel={handleCancel}
      onTempChange={setSelectedValue}
      label={content.label}
      description={content.description}
      disabled={disabled}
    />
  );
}
